import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { settingsApi } from '@api/endpoints/settings'
import { useUIStore } from '@store/uiStore'
import { themeRegistry } from '@themes/registry'
import type { UserPreferencesDto, Theme, Language, MeasurementUnit } from '@/types/settings'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'

export function PreferencesSection() {
  const [preferences, setPreferences] = useState<UserPreferencesDto | null>(null)
  const [language, setLanguage] = useState<Language>('en' as Language)
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(1) // Default to Imperial
  const [showLocationOnProfile, setShowLocationOnProfile] = useState(false)
  const [showOnlineStatus, setShowOnlineStatus] = useState(true)
  const themeMode = useUIStore((s) => s.themeMode)
  const themeName = useUIStore((s) => s.themeName)
  const setColorTheme = useUIStore((s) => s.setTheme)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load user preferences
  useEffect(() => {
    loadPreferences()
  }, [])

  const loadPreferences = async () => {
    try {
      setIsLoading(true)
      const data = await settingsApi.getUserPreferences()
      setPreferences(data)
      setLanguage(data.language)
      if (data.measurementUnit !== undefined) {
        setMeasurementUnit(data.measurementUnit)
      }
      setShowLocationOnProfile(data.showLocationOnProfile)
      setShowOnlineStatus(data.showOnlineStatus)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      const response = await settingsApi.updateUserPreferences({
        theme: (themeMode === 'dark' ? 'Dark' : 'Light') as Theme,
        language,
        measurementUnit,
        showLocationOnProfile,
        showOnlineStatus,
      })

      if (response.success) {
        setSuccessMessage('Preferences updated successfully!')
        await loadPreferences()
      } else {
        throw new Error(response.message || 'Failed to update preferences')
      }
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Color Theme Selection */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Color Theme
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose a color theme for the app. Unlock more themes by completing adventures!
        </Typography>

        <Grid container spacing={2}>
          {themeRegistry.getAllThemes().map((t) => (
            <Grid item xs={6} sm={4} md={3} key={t.id}>
              <Card
                variant="outlined"
                sx={{
                  borderWidth: themeName === t.id ? 2 : 1,
                  borderColor: themeName === t.id ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <CardActionArea onClick={() => setColorTheme(t.id)}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        mx: 'auto',
                        mb: 1,
                        background: t.preview
                          ? `linear-gradient(135deg, ${t.preview.primaryColor} 50%, ${t.preview.secondaryColor} 50%)`
                          : 'grey.300',
                        border: '2px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {themeName === t.id && (
                        <Check sx={{ color: '#fff', fontSize: 20, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t.name}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Language & Region */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Language & Region
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
                label="Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="de">Deutsch</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Measurement System</InputLabel>
              <Select
                value={measurementUnit}
                onChange={(e) => setMeasurementUnit(Number(e.target.value) as MeasurementUnit)}
                label="Measurement System"
              >
                <MenuItem value={0}>Metric (km, meters, etc.)</MenuItem>
                <MenuItem value={1}>Imperial (miles, feet, etc.)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadPreferences} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  )
}
